import { CircularProgress, Stack } from "@mui/material";
import { useOpenZendeskHelpCenter } from "@src/appV2/HelpCenter/useOpenZendeskHelpCenter";
import { useTu } from "@src/appV2/i18n/useTu";
import { Platform } from "@src/mobile/utils/platform";
import { useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import { Button } from "../components/Button";
import { Text } from "../components/Text";
import { getBackgroundBase } from "./themeHelpers";

const POST_OPEN_REDIRECT_PATH = "/v2/home";

/**
 * Renders the Help Center when the Zendesk SSO kill-switch is on. The internal
 * Help Center surface never mounts: instead we mint a fresh SSO token and open
 * the authenticated Zendesk Help Center in an in-app browser (Capacitor) or a
 * new tab (web), then bounce the user back to the home dashboard so the route
 * doesn't sit dangling.
 *
 * Capacitor's `Browser.open` is not gesture-restricted, so on native we trigger
 * the open automatically on mount. On web Safari blocks `window.open` outside
 * a user gesture, so we render a button and require an explicit click to keep
 * the popup chain valid.
 */
export function HelpCenterRedirectView() {
  const { tu } = useTu("helpCenter");
  const openZendeskHelpCenter = useOpenZendeskHelpCenter();
  const history = useHistory();
  const hasAutoOpenedRef = useRef(false);

  useEffect(() => {
    if (!Platform.isMobile || hasAutoOpenedRef.current) {
      return;
    }
    hasAutoOpenedRef.current = true;
    openZendeskHelpCenter();
    history.replace(POST_OPEN_REDIRECT_PATH);
  }, [history, openZendeskHelpCenter]);

  const handleOpenClick = () => {
    openZendeskHelpCenter();
    history.replace(POST_OPEN_REDIRECT_PATH);
  };

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={3}
      sx={(theme) => ({
        flex: 1,
        minHeight: 0,
        padding: theme.spacing(4),
        backgroundColor: getBackgroundBase(theme),
        borderRadius: Platform.isMobile ? undefined : theme.borderRadius?.large,
        boxShadow: Platform.isMobile ? undefined : theme.shadow?.card,
      })}
    >
      <CircularProgress aria-label={tu("Loading Help Center")} />
      <Text align="center">{tu("Opening Help Center…")}</Text>
      {!Platform.isMobile && (
        <Button onClick={handleOpenClick} variant="primary">
          {tu("Open Help Center")}
        </Button>
      )}
    </Stack>
  );
}
